import {useCallback, useState} from 'react'
import {useCustomerType, useCustomerId, useCustomer} from '@salesforce/commerce-sdk-react'
import {
    setTpaTrackingCookiesFromUrl,
    getTpaTrackingValuesFromCookies,
    formatTpaTrackingCookie,
    determineShouldUpdateTpaTrackingCookie,
    setRevTrackingCookiesFromCustomerData
} from './utils'
import {getTpaParamsFromUrl, getExpiryDateAndTimeForTpaCookies} from '../utils'
import useMutateCustomerTpaData from '@/hooks/commerce-sdk/useMutateCustomerTpaData'
import {TpaTrackingCookieValues} from '../types'
import {useSyncCookiesFromUrl} from '../useSyncTpaCookiesFromUrl'
import {useSyncCookiesFromCustomerData} from '../useSyncCookiesFromCustomerData'
import {useSyncCookieToCustomerProfile} from '../useSyncCookieToCustomerProfile'

const useTpaRevenueTrackingCookies = () => {
    const [tpaCookieValues, setTpaCookieValues] = useState<
        TpaTrackingCookieValues | null | undefined
    >(undefined)
    const {mutateCustomerTpaTrackingData} = useMutateCustomerTpaData()
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const shouldQueryCustomerData = !!customerId && isRegistered
    const {data: customerData, isLoading: isCustomerDataLoading} = useCustomer(
        {parameters: {customerId}},
        {enabled: shouldQueryCustomerData}
    )

    const {c_tpaTracking: tpaTrackingFromCustomerData} = customerData || {}
    // resolveAffiliate: this gate guards the only path to setTpaTrackingCookiesFromUrl,
    // so it must resolve legacy `a_aid` links too — otherwise a bare `?a_aid=` link
    // (no AFID) yields no params, the gate is false, and the cookie never gets set
    // (SFCC-11532). Branding callers stay un-flagged so partner branding is unaffected.
    const tpaParamsInUrl = getTpaParamsFromUrl({resolveAffiliate: true})
    const isTpaParamsInUrl = !!tpaParamsInUrl

    // Sets TPA tracking cookies from URL
    // This is used to set the cookies when the user clicks on a link with TPA tracking
    // and the cookies are not set yet

    useSyncCookiesFromUrl<TpaTrackingCookieValues>({
        isParamsInUrl: isTpaParamsInUrl,
        currentCookieValues: tpaCookieValues,
        getCookieValuesFromStorage: getTpaTrackingValuesFromCookies,
        paramsFromUrl: tpaParamsInUrl as TpaTrackingCookieValues,
        formatCookie: (val) => val,
        setCookiesFromUrl: setTpaTrackingCookiesFromUrl,
        setCookieValuesState: setTpaCookieValues,
        deepCompare: true // use deep equality here
    })

    // Sets TPA tracking cookies from customer data
    // This is used to set the cookies when the user is registered and the cookies are not set yet
    // and there are no TPA tracking values in the URL
    useSyncCookiesFromCustomerData<TpaTrackingCookieValues, string>({
        isParamsInUrl: isTpaParamsInUrl,
        isRegistered,
        customerDataValue: tpaTrackingFromCustomerData,
        setCookiesFromCustomerData: setRevTrackingCookiesFromCustomerData,
        getCookieValuesFromStorage: getTpaTrackingValuesFromCookies,
        setCookieValuesState: setTpaCookieValues
    })

    const formatCookie = useCallback((cookie: TpaTrackingCookieValues) => {
        if (!cookie || !cookie?.groupName) return ''
        const {expiryMs} = getExpiryDateAndTimeForTpaCookies(cookie.groupName)
        return formatTpaTrackingCookie({...cookie, expiryMs})
    }, [])

    // Determines if we should update customer profile with TPA tracking cookie
    useSyncCookieToCustomerProfile<string, string, TpaTrackingCookieValues>({
        isRegistered,
        isCustomerDataLoading,
        shouldQueryCustomerData,
        customerId,
        cookieValues: tpaCookieValues,
        customerProfileValue: tpaTrackingFromCustomerData,
        formatCookie,
        shouldUpdateCookie: determineShouldUpdateTpaTrackingCookie,
        mutateCustomerData: mutateCustomerTpaTrackingData
    })

    return null
}

export default useTpaRevenueTrackingCookies
